import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";

import {
  deletePresetItem,
  getPresetsList,
  handlePaginate,
} from "store/players/presets/slice";

import { tournamentsListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";

import { openModal } from "store/modals/slice";

import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import PresetsFilter from "./components/PresetsFilter";

import { NotificationTypes, showNotification } from "store/notifications/slice";

import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";

import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";

const PresetsList: React.FC = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [currentLimit, setLimit] = useState<number>(tournamentsListLimits[0]);

  const [search, setSearch] = useState<string>("");

  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([{ value: 3, label: "All" }]);

  const list = useAppSelector((store) => store.presets.list);
  const isListFetched = useAppSelector((store) => store.presets.isListFetched);
  const totalItems = useAppSelector((store) => store.presets.totalItems);
  const totalPages = useAppSelector((store) => store.presets.totalPages);
  const activePage = useAppSelector((store) => store.presets.activePage);
  const { refresh, isRefreshRequested } = useRefreshList({ sliceName: 'presets', isListFetched });

  const processingItemId = useAppSelector(
    (store) => store.presets.processingItemId,
  );

  const permissions = usePermissionHelper();

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };
  

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getPresetsList({
          limit: currentLimit,
          offset: activePage,

          title: search,

          isActive:
            active[0] && active[0].value !== 3
              ? Number(active[0].value)
              : undefined,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit]);

  const handlePrevClick = () => {
    if (activePage > 0) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage + 1 < totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.presets}&entityId=${id}`,
    );

    dispatch(
      openModal({
        activeModal: ModalTypes.presets,
      }),
    );
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.presets}`);

    dispatch(
      openModal({
        activeModal: ModalTypes.presets,
      }),
    );
  };

  const handleDeleteItem = async (id: number) => {
    const result = await dispatch(deletePresetItem(id ?? 0));

    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <PresetsFilter
        handleApply={() => {
          dispatch(handlePaginate(0));
        }}
        searchValue={search}
        handleSearch={setSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
      />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addPreset")}
                handleClick={() => {
                  handleOpenCreateModal();
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={10} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && (
                <EmptyState message={t("noPresetsFound")} />
              )}

              {list.length > 0 && (
                <Table
                  data={list}
                  idField="id"
                  activeRowId={searchParams.get("entityId")}
                  onDelete={(id) => handleDeleteItem(id)}
                  hasEdit={permissions?.update}
                  hasDelete={permissions?.delete}
                  hasView={permissions?.read && !permissions?.update}
                  hasOptions={permissions?.read || permissions?.update}
                  processingId={processingItemId}
                  hasCheckbox={false}
                  {...actionProps}
                />
              )}
            </div>

            {list.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);

                  dispatch(handlePaginate(0));
                }}
                limitsArray={tournamentsListLimits}
                activePage={activePage + 1}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PresetsList;
